import { useCallback, useMemo, useState } from 'react';
import { ScrollView } from 'react-native';
import type {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';
import {
  ColorValues,
  Flex,
  IconChevronRight,
  RemoteImage,
  Typography,
  useScreenSize,
  VerticalSpacing,
} from '@banhala/ui-kit';
import type { OverviewCategory, OverviewSubCategory } from 'shared/src/api';
import { isNil, isNotNil } from 'shared/src/utils';
import { useNavigator } from 'shared/src/utils/navigator';
import { RetailAdBanner } from './RetailAdBanner';
import { useLogClickHeadlineMore } from '../hooks/useLogClickHeadlineMore';
import { useLogClickIconList } from '../hooks/useLogClickIconList';

const ICON_SIZE = 32;
const COLUMNS = 2;

const SUB_CATEGORY_ITEM_GAP = 12;
const HORIZONTAL_PADDING = 16;

interface CategoryContentProps {
  categories: OverviewCategory[];
  contentRef: React.RefObject<ScrollView | null>;
  onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  onSectionLayout: (index: number) => (event: LayoutChangeEvent) => void;
}

export const CategoryContent = ({
  categories,
  contentRef,
  onScroll,
  onSectionLayout,
}: CategoryContentProps) => {
  const { navigator } = useNavigator();
  const { height: screenHeight } = useScreenSize();

  const [contentWidth, setContentWidth] = useState<number | null>(null);
  const logClickHeadlineMore = useLogClickHeadlineMore();
  const logClickIconList = useLogClickIconList();

  const handleCategoryHeaderPress = useCallback(
    (category: OverviewCategory) => {
      if (category.item.deeplink == null) return;

      logClickHeadlineMore(category);
      navigator.moveToCategory({ deeplink: category.item.deeplink });
    },
    [logClickHeadlineMore, navigator],
  );

  const handleSubCategoryPress = useCallback(
    (subCategory: OverviewSubCategory, category: OverviewCategory) => {
      logClickIconList(subCategory, category);
      navigator.moveToCategory({ deeplink: subCategory.item.deeplink });
    },
    [logClickIconList, navigator],
  );

  const categoryItemWidth = useMemo(() => {
    // const ratio = 1 - 1 / 2.27;
    // console.log('ratio', ratio, screenWidth);
    // const fullWidth = screenWidth * ratio - HORIZONTAL_PADDING * 2 - 12;
    // console.log('fullWidth', screenWidth, fullWidth, fullWidth / 2);
    if (isNil(contentWidth)) return 0;

    return (
      (contentWidth - HORIZONTAL_PADDING * 2 - SUB_CATEGORY_ITEM_GAP) / COLUMNS
    );
  }, [contentWidth]);

  return (
    <ScrollView
      ref={contentRef}
      style={{ flex: 2.27 }}
      onScroll={onScroll}
      scrollEventThrottle={16}
      showsVerticalScrollIndicator={false}
      onLayout={({ nativeEvent }) => {
        if (isNil(contentWidth)) setContentWidth(nativeEvent.layout.width);
      }}
    >
      <RetailAdBanner contentWidth={contentWidth} />
      {isNotNil(contentWidth) &&
        categories.map((category, index) => (
          <Flex key={category.item.sno} onLayout={onSectionLayout(index)}>
            {/* Category Header */}
            <Flex.Row
              alignItems="center"
              justifyContent="space-between"
              padding={{ horizontal: 16, vertical: 12 }}
              onPress={
                category.item.deeplink != null
                  ? () => handleCategoryHeaderPress(category)
                  : undefined
              }
            >
              <Flex.Row alignItems={'center'} gap={8}>
                <Flex
                  width={ICON_SIZE}
                  height={ICON_SIZE}
                  borderRadius={12}
                  overflow={'hidden'}
                >
                  <RemoteImage
                    uri={category.item.image}
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                  />
                </Flex>
                <Typography type="subtitle1" color="content_primary">
                  {category.item.name}
                </Typography>
              </Flex.Row>
              {category.item.deeplink != null && (
                <IconChevronRight size={16} color="content_secondary" />
              )}
            </Flex.Row>

            {/* SubCategory Grid */}
            <Flex.Row
              flexWrap="wrap"
              padding={{ left: HORIZONTAL_PADDING }}
              margin={{ bottom: 8 }}
            >
              {category.item.sub_category_list.map(subCategory => (
                <Flex.Row
                  key={subCategory.item.sno}
                  alignItems="center"
                  width={categoryItemWidth}
                  justifyContent={'space-between'}
                  borderBottomWidth={1}
                  borderColor={'border_tertiary'}
                  padding={{ vertical: 16 }}
                  margin={{ right: SUB_CATEGORY_ITEM_GAP }}
                  onPress={() => handleSubCategoryPress(subCategory, category)}
                >
                  <Typography
                    type="body3"
                    color="content_secondary"
                    numberOfLines={2}
                    textAlign="center"
                  >
                    {subCategory.item.name}
                  </Typography>
                  <IconChevronRight size={16} fill={ColorValues.gray30} />
                </Flex.Row>
              ))}
            </Flex.Row>

            {/* Section Divider */}
            {index < categories.length - 1 && <VerticalSpacing size={16} />}
          </Flex>
        ))}
      <VerticalSpacing size={screenHeight * 0.6} />
    </ScrollView>
  );
};
